import { memo } from 'react';
import { StyleSheet } from 'react-native';
import styled, { css } from 'styled-components/native';

import AvgRatingStars from '../../../elements/AvgRatingStars';
import Image from '../../../elements/Image';
import Placeholder from '../../../elements/Placeholder';
import { translate } from '../../../i18n';
import type { ProductReviewDetail } from '../../../types/ProductReviewDetailResponse';

interface Props {
  data: ProductReviewDetail | null;
}

const ImageSize = 80;
const ImageLoadingConfig = { width: ImageSize, height: ImageSize };

export default function Header({ data }: Props) {
  if (!data) {
    return <LoadingScene />;
  }

  return (
    <S.Container>
      <S.InfoContainer>
        <S.ImageContainer size={ImageSize}>
          <S.Image
            size={ImageSize}
            source={{ uri: data.image_url }}
            loadingConfig={ImageLoadingConfig}
          />
        </S.ImageContainer>
        <S.ContentRow>
          <S.Title numberOfLines={2} ellipsizeMode="tail">
            {data.title}
          </S.Title>
          <RatingStars rating={data.average_score} />
        </S.ContentRow>
      </S.InfoContainer>
      {data.review_count > 0 && (
        <S.ReviewsContainer>
          <S.ReviewsContent>
            {translate('item-total-reviews', {
              total: data.review_count < 100 ? data.review_count : '99+',
            })}
          </S.ReviewsContent>
        </S.ReviewsContainer>
      )}
    </S.Container>
  );
}

const LoadingScene = memo(() => {
  return (
    <S.Container>
      <S.InfoContainer>
        <S.ImageContainer size={ImageSize}>
          <Placeholder width={ImageSize} height={ImageSize} />
        </S.ImageContainer>
        <S.ContentRow>
          <Placeholder
            width={167}
            height={18}
            boxStyle={styles.titlePlaceholder}
          />
          <Placeholder width={100} height={18} />
        </S.ContentRow>
      </S.InfoContainer>
    </S.Container>
  );
});

const RatingStars = memo(({ rating }: { rating: number }) => {
  return (
    <S.StarContainer>
      <AvgRatingStars rating={rating} />
      <S.StarValue>{rating.toFixed(1)}</S.StarValue>
    </S.StarContainer>
  );
});

const styles = StyleSheet.create({
  titlePlaceholder: {
    marginBottom: 8,
  },
});

const indicatorTextBasicStyle = css`
  font-size: 15px;
  line-height: 21px;
`;

const S = {
  Container: styled.View`
    background-color: ${({ theme }) => theme.palette.white};
  `,
  InfoContainer: styled.View`
    align-items: flex-start;
    border-bottom-width: 1px;
    border-color: ${({ theme }) => theme.palette.neutralLight};
    flex-direction: row;
    padding: 10px 15px;
  `,
  ImageContainer: styled.View<{ size: number }>`
    border-radius: 3px;
    height: ${({ size }) => size}px;
    overflow: hidden;
    width: ${({ size }) => size}px;
  `,
  Image: styled(Image)<{ size: number }>`
    height: ${({ size }) => size}px;
    width: ${({ size }) => size}px;
  `,
  ContentRow: styled.View`
    flex-direction: column;
    flex: 1;
    margin-left: 10px;
  `,
  Title: styled.Text`
    ${indicatorTextBasicStyle};
    color: ${({ theme }) => theme.palette.warrior};
    margin-bottom: 4px;
  `,
  StarContainer: styled.View`
    align-items: center;
    flex-direction: row;
  `,
  StarValue: styled.Text`
    color: ${({ theme }) => theme.palette.coral};
    font-size: 12px;
    line-height: 17px;
    margin-left: 8px;
  `,
  ReviewsContainer: styled.View`
    border-bottom-width: 1px;
    border-color: ${({ theme }) => theme.palette.neutralLight};
    padding: 12px 15px;
  `,
  ReviewsContent: styled.Text`
    ${indicatorTextBasicStyle};
    color: ${({ theme }) => theme.palette.warrior};
  `,
};
